// Partner requests waiting for a platform administrator: trusted networks
// (Phase 8), purposes (Phase 9), the reasons a company may look people up
// for, which the individual reads with every request, and M-Pesa tills for
// till payments (Phase 11), which can also be suspended.
// A company admin registers the public addresses its servers call from; a
// network outside the Kenyan address ranges is approved only with an explicit
// exception (for example servers hosted abroad), recorded with the decision.
import {
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { FieldBadges } from "../../shared/ui/format";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { AdminLayout } from "./AdminLayout";

type Network = Schemas["TrustedNetwork"];

const KEY = ["admin", "trusted-networks"] as const;

export function AdminNetworksPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")}>
      <Stack gap="lg">
        <Networks />
        <Purposes />
        <Tills />
      </Stack>
    </AdminLayout>
  );
}

function Networks() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pending = useQuery({
    queryKey: KEY,
    queryFn: () => unwrap(api.GET("/admin/trusted-networks", { params: { query: { limit: 200 } } })),
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const items = pending.data?.items ?? [];

  return (
    <Paper withBorder p="md" radius="md" data-testid="admin-networks">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2} size="h4">
            {t("admin_networks.title")}
          </Title>
          <Text size="sm" c="dimmed">
            {t("admin_networks.intro")}
          </Text>
        </Stack>
        {notice && <Notice>{notice}</Notice>}
        <FormError message={error} />
        {pending.isPending ? (
          <Loader size="sm" aria-label={t("common.loading")} />
        ) : pending.isError ? (
          <FormError message={errorMessage(pending.error, t("error.generic"))} />
        ) : items.length === 0 ? (
          <Text size="sm" c="dimmed" data-testid="networks-empty">
            {t("admin_networks.empty")}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing="sm" aria-label={t("admin_networks.title")}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("admin_companies.name")}</Table.Th>
                  <Table.Th>{t("networks.network")}</Table.Th>
                  <Table.Th>{t("networks.location")}</Table.Th>
                  <Table.Th>{t("networks.requested")}</Table.Th>
                  <Table.Th>{t("admin_companies.actions")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((n) => (
                  <PendingRow
                    key={n.id}
                    network={n}
                    onDecided={(msg) => {
                      setError(null);
                      setNotice(msg);
                      void queryClient.invalidateQueries({ queryKey: KEY });
                    }}
                    onError={(msg) => {
                      setNotice(null);
                      setError(msg);
                    }}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Paper>
  );
}

function PendingRow({
  network: n,
  onDecided,
  onError,
}: {
  network: Network;
  onDecided: (notice: string) => void;
  onError: (error: string) => void;
}) {
  const { t } = useTranslation();
  const f = useFormat();
  const [outside, setOutside] = useState(false);
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const name = n.company_name ?? t("admin_credit.unknown_company");
  const vars = { cidr: n.cidr, name };

  const decide = async (decision: "approve" | "reject") => {
    setBusy(decision);
    const { data, error } = await api.POST("/admin/trusted-networks/{network_id}/decision", {
      params: { path: { network_id: n.id } },
      body: { decision, ...(decision === "approve" && !n.in_kenya ? { outside_kenya: outside } : {}) },
    });
    setBusy(null);
    if (!data) return onError(errorMessage(error, t("error.generic")));
    onDecided(decision === "approve" ? t("admin_networks.approved", vars) : t("admin_networks.rejected", vars));
  };

  return (
    <Table.Tr data-testid="network-row">
      <Table.Td>{name}</Table.Td>
      <Table.Td>
        <Text size="sm" ff="monospace">
          {n.cidr}
        </Text>
        {n.label && (
          <Text size="xs" c="dimmed">
            {n.label}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Stack gap={6}>
          <Badge color={n.in_kenya ? "green" : "orange"} w="fit-content">
            {n.in_kenya ? t("networks.in_kenya") : t("networks.outside_kenya")}
          </Badge>
          {!n.in_kenya && (
            <Checkbox
              size="xs"
              label={t("admin_networks.allow_outside")}
              checked={outside}
              onChange={(e) => setOutside(e.currentTarget.checked)}
            />
          )}
        </Stack>
      </Table.Td>
      <Table.Td>{f.dateTime(n.requested_at)}</Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Button
            size="xs"
            loading={busy === "approve"}
            disabled={busy !== null}
            onClick={() => void decide("approve")}
            aria-label={t("admin_networks.approve_for", vars)}
          >
            {t("admin_networks.approve")}
          </Button>
          <Button
            size="xs"
            variant="light"
            color="red"
            loading={busy === "reject"}
            disabled={busy !== null}
            onClick={() => void decide("reject")}
            aria-label={t("admin_networks.reject_for", vars)}
          >
            {t("admin_networks.reject")}
          </Button>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

type Purpose = Schemas["Purpose"];
const PURPOSES_KEY = ["admin", "purposes"] as const;

function Purposes() {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const pending = useQuery({
    queryKey: PURPOSES_KEY,
    queryFn: () => unwrap(api.GET("/admin/purposes", { params: { query: { limit: 200 } } })),
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const items = pending.data?.items ?? [];

  const decide = async (p: Purpose, decision: "approve" | "reject") => {
    setError(null);
    setNotice(null);
    setBusy(p.id + decision);
    const { data, error } = await api.POST("/admin/purposes/{purpose_id}/decision", {
      params: { path: { purpose_id: p.id } },
      body: { decision },
    });
    setBusy(null);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    const vars = { code: p.code, name: p.company_name ?? "" };
    setNotice(decision === "approve" ? t("admin_purposes.approved", vars) : t("admin_purposes.rejected", vars));
    void queryClient.invalidateQueries({ queryKey: PURPOSES_KEY });
  };

  return (
    <Paper withBorder p="md" radius="md" data-testid="admin-purposes">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2} size="h4">
            {t("admin_purposes.title")}
          </Title>
          <Text size="sm" c="dimmed">
            {t("admin_purposes.intro")}
          </Text>
        </Stack>
        {notice && <Notice>{notice}</Notice>}
        <FormError message={error} />
        {pending.isPending ? (
          <Loader size="sm" aria-label={t("common.loading")} />
        ) : pending.isError ? (
          <FormError message={errorMessage(pending.error, t("error.generic"))} />
        ) : items.length === 0 ? (
          <Text size="sm" c="dimmed" data-testid="purposes-empty">
            {t("admin_purposes.empty")}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing="sm" aria-label={t("admin_purposes.title")}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("admin_companies.name")}</Table.Th>
                  <Table.Th>{t("purposes.code")}</Table.Th>
                  <Table.Th>{t("purposes.description")}</Table.Th>
                  <Table.Th>{t("purposes.fields")}</Table.Th>
                  <Table.Th>{t("admin_companies.actions")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((p) => {
                  const vars = { code: p.code, name: p.company_name ?? "" };
                  return (
                    <Table.Tr key={p.id} data-testid="purpose-row">
                      <Table.Td>
                        {p.company_name ?? t("admin_credit.unknown_company")}
                        <Text size="xs" c="dimmed">
                          {f.dateTime(p.requested_at)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {p.code}
                        </Text>
                      </Table.Td>
                      <Table.Td>{p.description}</Table.Td>
                      <Table.Td>
                        <FieldBadges fields={p.fields} />
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <Button
                            size="xs"
                            loading={busy === p.id + "approve"}
                            disabled={busy !== null}
                            onClick={() => void decide(p, "approve")}
                            aria-label={t("admin_purposes.approve_for", vars)}
                          >
                            {t("admin_networks.approve")}
                          </Button>
                          <Button
                            size="xs"
                            variant="light"
                            color="red"
                            loading={busy === p.id + "reject"}
                            disabled={busy !== null}
                            onClick={() => void decide(p, "reject")}
                            aria-label={t("admin_purposes.reject_for", vars)}
                          >
                            {t("admin_networks.reject")}
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Paper>
  );
}

type Till = Schemas["Till"];
type TillStatus = Till["status"];
const TILLS_KEY = ["admin", "tills"] as const;
const TILL_COLOUR: Record<TillStatus, string> = { pending: "yellow", approved: "green", suspended: "red" };

function Tills() {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<TillStatus>("pending");
  const tills = useQuery({
    queryKey: [...TILLS_KEY, status],
    queryFn: () => unwrap(api.GET("/admin/tills", { params: { query: { status, limit: 200 } } })),
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const items = tills.data?.items ?? [];

  const decide = async (till: Till, decision: "approve" | "suspend") => {
    setError(null);
    setNotice(null);
    const reason = (reasons[till.company_id] ?? "").trim();
    setBusy(till.company_id + decision);
    const { data, error } = await api.POST("/admin/tills/{company_id}/decision", {
      params: { path: { company_id: till.company_id } },
      body: decision === "approve" ? { decision } : { decision, reason },
    });
    setBusy(null);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    const vars = { name: till.company_name ?? "" };
    setNotice(decision === "approve" ? t("admin_tills.approved", vars) : t("admin_tills.suspended", vars));
    void queryClient.invalidateQueries({ queryKey: TILLS_KEY });
  };

  return (
    <Paper withBorder p="md" radius="md" data-testid="admin-tills">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2} size="h4">
            {t("admin_tills.title")}
          </Title>
          <Text size="sm" c="dimmed">
            {t("admin_tills.intro")}
          </Text>
        </Stack>
        <SegmentedControl
          aria-label={t("admin_tills.show")}
          value={status}
          onChange={(v) => setStatus(v as TillStatus)}
          data={(["pending", "approved", "suspended"] as const).map((s) => ({
            value: s,
            label: t(`till.status_${s}`),
          }))}
          w="fit-content"
        />
        {notice && <Notice>{notice}</Notice>}
        <FormError message={error} />
        {tills.isPending ? (
          <Loader size="sm" aria-label={t("common.loading")} />
        ) : tills.isError ? (
          <FormError message={errorMessage(tills.error, t("error.generic"))} />
        ) : items.length === 0 ? (
          <Text size="sm" c="dimmed" data-testid="tills-empty">
            {t("admin_tills.empty")}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing="sm" aria-label={t("admin_tills.title")}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("admin_companies.name")}</Table.Th>
                  <Table.Th>{t("admin_tills.till")}</Table.Th>
                  <Table.Th>{t("till.environment")}</Table.Th>
                  <Table.Th>{t("admin_companies.actions")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((till) => {
                  const vars = { name: till.company_name ?? "" };
                  const id = till.company_id;
                  return (
                    <Table.Tr key={id} data-testid="till-row">
                      <Table.Td>
                        {till.company_name ?? t("admin_credit.unknown_company")}
                        <Text size="xs" c="dimmed">
                          {f.dateTime(till.updated_at)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {t(`till.kind_${till.kind}`)} {till.till_number}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {t("till.short_code")}: {till.short_code}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={till.environment === "production" ? "blue" : "gray"}>
                          {t(`till.env_${till.environment}`)}
                        </Badge>
                        <Badge color={TILL_COLOUR[till.status]} ml="xs">
                          {t(`till.status_${till.status}`)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap" align="flex-end">
                          {till.status !== "approved" && (
                            <Button
                              size="xs"
                              loading={busy === id + "approve"}
                              disabled={busy !== null}
                              onClick={() => void decide(till, "approve")}
                              aria-label={t("admin_tills.approve_for", vars)}
                            >
                              {t("admin_networks.approve")}
                            </Button>
                          )}
                          {till.status !== "suspended" && (
                            <>
                              <TextInput
                                size="xs"
                                aria-label={t("admin_tills.reason")}
                                placeholder={t("admin_tills.reason")}
                                value={reasons[id] ?? ""}
                                onChange={(e) => {
                                  const v = e.currentTarget.value;
                                  setReasons((r) => ({ ...r, [id]: v }));
                                }}
                                maxLength={200}
                              />
                              <Button
                                size="xs"
                                variant="light"
                                color="red"
                                loading={busy === id + "suspend"}
                                disabled={busy !== null || !(reasons[id] ?? "").trim()}
                                onClick={() => void decide(till, "suspend")}
                                aria-label={t("admin_tills.suspend_for", vars)}
                              >
                                {t("admin_tills.suspend")}
                              </Button>
                            </>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Paper>
  );
}
